import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/useSeo";
import {
  UserFocus,
  Buildings,
  Headset,
  ShieldCheck,
  Sparkle,
  Target,
  Eye,
  CheckCircle,
  Certificate,
  Flag,
  Rocket,
} from "@phosphor-icons/react";

const WHY_CARDS = [
  {
    icon: UserFocus,
    title: "Individual Returns",
    desc: "W-2 earners, freelancers & independent contractors get personalized guidance and timely filing — federal, state, and multi-state.",
  },
  {
    icon: Buildings,
    title: "Business & Corporate",
    desc: "LLCs, partnerships and corporations benefit from end-to-end compliance, K-1s, depreciation, and quarterly estimates.",
  },
  {
    icon: Headset,
    title: "Advisory & Support",
    desc: "Year-round guidance on amendments, refunds, IRS notices and multi-state issues — not just a once-a-year transaction.",
  },
];

const STATS = [
  ["12,400+", "Returns filed"],
  ["$18.7M", "Refunds delivered"],
  ["40+", "Countries served"],
  ["4.9/5", "Client rating"],
];

const TIMELINE = [
  {
    year: "2022",
    icon: Flag,
    title: "Founded",
    desc: "Taxfree Filer LLP is formed by three tax partners around one thesis: modern tax operations shouldn't feel like a fire drill every April.",
  },
  {
    year: "2023",
    icon: Certificate,
    title: "IRS EFIN registered",
    desc: "Authorized as an IRS e-File Provider. All preparers hold active PTINs; first 1,000 client returns e-filed with a 99.6% acceptance rate.",
  },
  {
    year: "2024",
    icon: ShieldCheck,
    title: "SOC 2 aligned",
    desc: "Controls modeled on SOC 2 Type II: MFA-ready sign-in, 256-bit TLS everywhere, audit-logged admin actions, 7-year retention.",
  },
  {
    year: "2025",
    icon: Rocket,
    title: "Mission Control launched",
    desc: "Our operations platform goes live — Kanban workflow, IRS notice register, secure client portal and real-time firm analytics in one product.",
  },
];

export default function About() {
  useSeo({
    title: "About Taxfree Filer LLP | US Tax Filing Specialist for NRIs",
    description: "Learn about Taxfree Filer LLP — our mission, vision, values, and why individuals and enterprise firms trust us with their US tax filings.",
    robots: "index,follow",
    canonical: "https://taxfreefiler.com/about",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b tf-hairline">
        <div className="absolute inset-0 tf-grid opacity-60" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">About</div>
            <h1 className="mt-3 font-[Chivo] text-5xl lg:text-6xl font-black text-[#0A2540] tracking-tight" data-testid="about-title">
              About Taxfree Filer LLP
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              We provide expert US tax services with transparency, precision and care — from individual 1040s to enterprise corporate filings and IRS notice defense.
            </p>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2">
          <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">01 · Who We Are</div>
          <h2 className="mt-3 font-[Chivo] text-3xl lg:text-4xl font-black text-[#0A2540] tracking-tight">A boutique tax firm, engineered like a product team.</h2>
        </div>
        <div className="lg:col-span-3 space-y-4 text-slate-700 leading-relaxed">
          <p>
            At Taxfree Filer LLP we simplify the complex world of US tax filing with precision, transparency, and trust. Our licensed CPAs and Enrolled Agents bring a decade of Big-4 and boutique experience, delivered inside a modern operations platform we call <span className="font-semibold text-[#0A2540]">Mission Control</span>.
          </p>
          <p>
            Whether you're a salaried professional, a small business owner, an NRI investing in US assets, or part of an enterprise workforce, our team ensures your returns are filed accurately and on time. We specialize in:
          </p>
          <ul className="space-y-2 mt-2">
            <li className="flex items-start gap-2"><CheckCircle size={18} weight="fill" className="text-[#0A2540] mt-0.5" /><span><strong className="text-[#0A2540]">Individual Tax Filing</strong> — personalized support for W-2 earners, freelancers and independent contractors.</span></li>
            <li className="flex items-start gap-2"><CheckCircle size={18} weight="fill" className="text-[#0A2540] mt-0.5" /><span><strong className="text-[#0A2540]">Business Tax Filing</strong> — end-to-end solutions for LLCs, partnerships and corporations.</span></li>
            <li className="flex items-start gap-2"><CheckCircle size={18} weight="fill" className="text-[#0A2540] mt-0.5" /><span><strong className="text-[#0A2540]">Corporate Programs</strong> — seamless tax filing for your employees through trusted partnerships with our experienced team.</span></li>
            <li className="flex items-start gap-2"><CheckCircle size={18} weight="fill" className="text-[#0A2540] mt-0.5" /><span><strong className="text-[#0A2540]">International & NRI Filings</strong> — ITIN, FBAR/FATCA, treaty positions and streamlined disclosures.</span></li>
          </ul>
          <p>
            Our commitment to <span className="font-semibold text-[#0A2540]">data security</span>, <span className="font-semibold text-[#0A2540]">professional ethics</span> and <span className="font-semibold text-[#0A2540]">client satisfaction</span> sets us apart. We don't just file taxes — we build long-term relationships based on clarity and accountability.
          </p>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y tf-hairline bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map(([v, l]) => (
            <div key={l} className="border-l-2 border-[#D32F2F] pl-4">
              <div className="font-[Chivo] text-3xl font-black text-[#0A2540]">{v}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20 grid md:grid-cols-2 gap-6">
        <div className="rounded-lg border tf-hairline bg-white p-8 lg:p-10">
          <div className="h-11 w-11 rounded-md grid place-items-center bg-[#D32F2F]/10 text-[#D32F2F]">
            <Target size={22} weight="duotone" />
          </div>
          <div className="mt-6 text-xs font-mono uppercase tracking-widest text-[#D32F2F]">02 · Our Mission</div>
          <h3 className="mt-2 font-[Chivo] text-2xl lg:text-3xl font-black text-[#0A2540] tracking-tight">Make US tax filing simple, transparent and fast.</h3>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Our mission is to simplify US tax filing for individuals and businesses worldwide. We leverage technology and licensed tax experts to deliver fast, accurate, transparent tax services — with pricing you can see before you file.
          </p>
        </div>
        <div className="rounded-lg border tf-hairline bg-[#0A2540] text-white p-8 lg:p-10">
          <div className="h-11 w-11 rounded-md grid place-items-center bg-white/10 text-[#D32F2F]">
            <Eye size={22} weight="duotone" />
          </div>
          <div className="mt-6 text-xs font-mono uppercase tracking-widest text-[#D32F2F]">03 · Our Vision</div>
          <h3 className="mt-2 font-[Chivo] text-2xl lg:text-3xl font-black tracking-tight">Reinvent the tax filing experience.</h3>
          <p className="mt-4 text-slate-200 leading-relaxed">
            To revolutionize the tax filing experience by harnessing technology and human expertise — delivering unmatched service quality and peace of mind for every client, in every timezone.
          </p>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-slate-50 border-y tf-hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">04 · Why Choose Us</div>
              <h2 className="mt-3 font-[Chivo] text-3xl lg:text-4xl font-black text-[#0A2540] tracking-tight">A trusted partner for every filing scenario.</h2>
            </div>
            <p className="max-w-md text-slate-600">Certified professionals, clear pricing and round-the-clock support — for individuals, businesses and international filers.</p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {WHY_CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="rounded-lg border tf-hairline bg-white p-8" data-testid={`why-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="h-11 w-11 rounded-md grid place-items-center bg-[#0A2540] text-white">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <h3 className="mt-6 font-[Chivo] text-xl font-bold text-[#0A2540]">{c.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* IRS AUTHORIZED */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
        <div className="rounded-lg border tf-hairline bg-white p-8 lg:p-10 grid lg:grid-cols-3 gap-8 items-center">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-md bg-[#0A2540] text-white grid place-items-center shrink-0">
              <Certificate size={30} weight="duotone" />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Authorized</div>
              <div className="mt-1 font-[Chivo] font-black text-[#0A2540] text-lg leading-tight">IRS e-File Provider</div>
              <div className="text-xs text-slate-500 mt-1">EFIN registered · PTIN verified</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-md bg-[#D32F2F] text-white grid place-items-center shrink-0">
              <ShieldCheck size={30} weight="duotone" />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Security</div>
              <div className="mt-1 font-[Chivo] font-black text-[#0A2540] text-lg leading-tight">SOC 2 aligned</div>
              <div className="text-xs text-slate-500 mt-1">256-bit TLS · MFA-ready · audit logged</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-md bg-slate-100 text-[#0A2540] grid place-items-center shrink-0">
              <Sparkle size={30} weight="duotone" />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Guarantee</div>
              <div className="mt-1 font-[Chivo] font-black text-[#0A2540] text-lg leading-tight">Accuracy backed</div>
              <div className="text-xs text-slate-500 mt-1">Free amendment for our filing errors.</div>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-slate-50 border-y tf-hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">05 · Our Journey</div>
            <h2 className="mt-3 font-[Chivo] text-3xl lg:text-4xl font-black text-[#0A2540] tracking-tight">Milestones along the way.</h2>
            <p className="mt-3 text-slate-600">Four years, four milestones — quiet compounding beats loud launches.</p>
          </div>

          {/* Desktop: horizontal · Mobile: vertical */}
          <div className="mt-14 relative">
            <div className="hidden lg:block absolute left-0 right-0 top-8 h-px bg-slate-200" aria-hidden />
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative" data-testid="about-timeline">
              {TIMELINE.map((m, idx) => {
                const Icon = m.icon;
                return (
                  <li key={m.year} className="relative" data-testid={`timeline-${m.year}`}>
                    <div className="h-16 w-16 rounded-full bg-white border-2 border-[#0A2540] text-[#0A2540] grid place-items-center relative z-10">
                      <Icon size={26} weight="duotone" />
                    </div>
                    <div className="mt-5 inline-flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Step 0{idx + 1}</span>
                      <span className="h-1 w-1 rounded-full bg-[#D32F2F]" />
                      <span className="font-mono text-xs text-[#D32F2F] font-bold tracking-widest">{m.year}</span>
                    </div>
                    <h3 className="mt-2 font-[Chivo] text-xl font-bold text-[#0A2540]">{m.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.desc}</p>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A2540] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-mono uppercase tracking-widest">
              <Sparkle size={12} weight="fill" className="text-[#D32F2F]" /> Free to start
            </div>
            <h2 className="mt-5 font-[Chivo] text-3xl lg:text-5xl font-black tracking-tight">Start your preparation for free. Pay only when you file.</h2>
            <p className="mt-4 text-slate-300 max-w-lg">
              Get expert tax filing services with Taxfree Filer LLP. Begin today at no cost — pay only when you approve your return for filing.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/auth?tab=login"><Button size="lg" variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-[#0A2540]" data-testid="about-login">Login</Button></Link>
            <Link to="/auth?tab=register"><Button size="lg" className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 h-12 px-6" data-testid="about-register">Register</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
